import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  User,
  LogOut,
  Menu,
  X,
  Shield,
  DollarSign,
  Users,
  Bell,
  PenTool,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ClientHome from '../components/client/ClientHome';
import ClientDocuments from '../components/client/ClientDocuments';
import ClientMessages from '../components/client/ClientMessages';
import ClientProfile from '../components/client/ClientProfile';
import ClientInvoices from '../components/client/ClientInvoices';
import ClientTeam from '../components/client/ClientTeam';
import ClientDocumentation from '../components/client/ClientDocumentation';
import OnboardingTour from '../components/ui/OnboardingTour';
import { clientOnboardingSteps } from '../config/onboardingSteps';
import api from '../utils/api';

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [notifications, setNotifications] = useState({
    pendingSignatures: [],
    unreadMessages: 0
  });

  const fetchNotifications = async () => {
    try {
      const [sigPending, sigViewed, messages] = await Promise.all([
        api.get('/signature-requests?status=pending').catch(() => ({ data: [] })),
        api.get('/signature-requests?status=viewed').catch(() => ({ data: [] })),
        api.get('/messages/unread').catch(() => ({ data: { count: 0 } }))
      ]);
      
      setNotifications({
        pendingSignatures: [...(sigPending.data || []), ...(sigViewed.data || [])],
        unreadMessages: messages.data?.count || 0
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if user has completed onboarding
  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem('client_onboarding_completed');
    if (!hasCompletedOnboarding && location.pathname === '/dashboard') {
      // Small delay to let the page render first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, [location.pathname]);

  const totalNotifications = notifications.pendingSignatures.length + notifications.unreadMessages;

  const navigation = [
    { name: 'Dashboard', href: '/client/dashboard', icon: LayoutDashboard },
    { name: 'Documents', href: '/client/documents', icon: FileText },
    { name: 'Invoices', href: '/client/invoices', icon: DollarSign },
    { name: 'Messages', href: '/client/messages', icon: MessageSquare },
    { name: 'Team', href: '/client/team', icon: Users },
    { name: 'Profile', href: '/client/profile', icon: User },
    { name: 'Help', href: '/client/help', icon: HelpCircle },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center">
              <img 
                src="/ppmt-logo.png" 
                alt="PPMT Strategic Group" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-teal-700 font-semibold text-sm">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${active 
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg shadow-teal-500/30' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Help & Logout */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <Button
              data-testid="start-tour-button"
              onClick={() => setShowOnboarding(true)}
              variant="ghost"
              className="w-full justify-start text-teal-600 hover:text-teal-700 hover:bg-teal-50"
            >
              <HelpCircle className="w-5 h-5 mr-3" />
              Take a Tour
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 mr-4"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {navigation.find(item => isActive(item.href))?.name || 'Client Portal'}
            </h1>
          </div>
          
          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="w-6 h-6 text-gray-600" />
                {totalNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {totalNotifications > 9 ? '9+' : totalNotifications}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {totalNotifications > 0 && (
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                    {totalNotifications} pending
                  </span>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {totalNotifications === 0 ? (
                <div className="py-6 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No pending notifications</p>
                </div>
              ) : (
                <>
                  {/* Pending Signatures */}
                  {notifications.pendingSignatures.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <PenTool className="w-3 h-3" />
                        Documents to Sign ({notifications.pendingSignatures.length})
                      </DropdownMenuLabel>
                      {notifications.pendingSignatures.slice(0, 3).map((sig) => (
                        <DropdownMenuItem
                          key={sig.id}
                          onClick={() => navigate(`/sign/${sig.id}`)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-start gap-3 py-1">
                            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <PenTool className="w-4 h-4 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{sig.title}</p>
                              <p className="text-xs text-gray-500">
                                {sig.document_type?.replace('_', ' ')} 
                                {sig.expires_at && ` • Expires ${new Date(sig.expires_at).toLocaleDateString()}`}
                              </p>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      {notifications.pendingSignatures.length > 3 && (
                        <DropdownMenuItem
                          onClick={() => navigate('/client/dashboard')}
                          className="text-center text-sm text-teal-600 hover:text-teal-700"
                        >
                          View all {notifications.pendingSignatures.length} pending signatures
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  
                  {/* Unread Messages */}
                  {notifications.unreadMessages > 0 && (
                    <>
                      {notifications.pendingSignatures.length > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onClick={() => navigate('/client/messages')}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-3 py-1">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <MessageSquare className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {notifications.unreadMessages} Unread Message{notifications.unreadMessages > 1 ? 's' : ''}
                            </p>
                            <p className="text-xs text-gray-500">Click to view messages</p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/client/dashboard" />} />
            <Route path="/dashboard" element={<ClientHome />} />
            <Route path="/documents" element={<ClientDocuments />} />
            <Route path="/invoices" element={<ClientInvoices />} />
            <Route path="/messages" element={<ClientMessages />} />
            <Route path="/team" element={<ClientTeam />} />
            <Route path="/profile" element={<ClientProfile />} />
            <Route path="/help" element={<ClientDocumentation />} />
          </Routes>
        </main>
      </div>

      {/* Onboarding Tour */}
      <OnboardingTour
        steps={clientOnboardingSteps}
        isOpen={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
        storageKey="client_onboarding_completed"
      />
    </div>
  );
};

export default ClientDashboard;
